import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number | null;
}

const EmployeeBiodataModal: React.FC<Props> = ({ isOpen, onClose, employeeId }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [blobData, setBlobData] = useState<Blob | null>(null);
  const pdfUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !employeeId) return;
    let mounted = true;
    setLoading(true);
    setPdfUrl(null);
    setBlobData(null);

    (async () => {
      try {
        const res = await api.get(`/employees/${employeeId}/biodata`, { responseType: 'blob' });
        if (!mounted) return;
        const blob = res.data as Blob;
        const url = URL.createObjectURL(blob);
        setBlobData(blob);
        setPdfUrl(url);
        pdfUrlRef.current = url;
      } catch (err) {
        console.error('Failed to load biodata PDF', err);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, [isOpen, employeeId]);

  const handleDownload = () => {
    if (!blobData) return;
    const url = URL.createObjectURL(blobData);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employee_${employeeId}_biodata.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium">Employee Biodata</h3>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">Close</button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : pdfUrl ? (
            <iframe src={pdfUrl} title="Employee Biodata" className="w-full h-[70vh] border" />
          ) : (
            <div className="text-center py-8">No biodata available</div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
          <button onClick={handleDownload} className="px-4 py-2 bg-[#06402B] text-white rounded" disabled={!blobData}>
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeBiodataModal;
